import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Loading from "../features/Loading";

// Roles allowed to view the post-checkout invoice page. Matches the same
// list used in Payment.js to decide who sees the Stripe plan-selection flow.
const ADMIN_ROLES = ["Administrator", "Athletic Director/Administrator"];

// All Sports users (admin or not) land on the roster upload page first, so
// they're forced to bring in athletes before /athletes can show anything.
// EMR has no roster-upload step, so EMR users are unaffected and still land
// on /emr-dashboard as before.
const getLandingRoute = (userProfile) => {
    if (userProfile?.iiss_type === "emr") {
        return "/emr-dashboard";
    }
    return "/add-new-athletes";
};

const ProtectedRoute = () => {
    const { ready, isAuthenticated, hasPaid, isVerified } = useAuth();

    if (!ready || hasPaid === null || isVerified === null) {
        return <Loading />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    if (!hasPaid && !isVerified) {
        return <Navigate to="/payment" replace />;
    }

    return <Outlet />;
};

const PaidRoute = () => {
    const { ready, isAuthenticated, hasPaid, isVerified, userProfile} = useAuth();

    if (!ready) {
        return <Loading />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    if (hasPaid || isVerified) {
        return <Navigate to={getLandingRoute(userProfile)} replace />;
    }

    return <Outlet />;
};

const PublicRoute = () => {
    const { ready, isAuthenticated, userProfile } = useAuth();

    if (!ready) {
        return <Loading />;
    }

    if (isAuthenticated) {
        return <Navigate to={getLandingRoute(userProfile)} replace />;
    }

    return <Outlet />;
};

// Gates admin-only pages (e.g. /invoice). Must be nested inside
// ProtectedRoute so ready/isAuthenticated/hasPaid are already handled —
// this only adds the role check on top.
const AdminRoute = () => {
    const { ready, userProfile } = useAuth();

    if (!ready) {
        return <Loading />;
    }

    if (!ADMIN_ROLES.includes(userProfile?.user_type)) {
        return <Navigate to={getLandingRoute(userProfile)} replace />;
    }

    return <Outlet />;
};

export { PublicRoute, PaidRoute, ProtectedRoute, AdminRoute, getLandingRoute };